import { Spinner } from '@shared/components/ui/Spinner';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { ConfigurationPageHeader } from '@modules/configuration/components/ConfigurationPageHeader';
import {
  configurationPageClass,
  configurationPageContentClass,
} from '@modules/configuration/components/configurationLayout';
import { useTranslation } from 'react-i18next';
import type { Courier, ShipmentStatusMapping } from '../../models/shipmentStatusMapping.types';
import { FiltersSection, MapStatusesButton } from '../interaction';
import { ShipmentStatusMappingEmptyState } from './ShipmentStatusMappingEmptyState';
import { ShipmentStatusMappingsList } from './ShipmentStatusMappingsList';

interface ShipmentStatusMappingViewProps {
  mappings: ShipmentStatusMapping[];
  filteredTotal: number;
  allMappingsCount: number;
  couriers: Courier[];
  search: string;
  courierId: string;
  statusId: string;
  hasActiveFilters: boolean;
  canUpdate?: boolean;
  isFetchingFilters?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onFiltersChange: (updates: { search?: string; courier?: string; status?: string }) => void;
  onEditMapping: (mapping: ShipmentStatusMapping) => void;
  onUnmapMapping: (mapping: ShipmentStatusMapping) => void;
  onMapStatuses: () => void;
}

export function ShipmentStatusMappingView({
  mappings,
  filteredTotal,
  allMappingsCount,
  couriers,
  search,
  courierId,
  statusId,
  hasActiveFilters,
  canUpdate = false,
  isFetchingFilters = false,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onFiltersChange,
  onEditMapping,
  onUnmapMapping,
  onMapStatuses,
}: ShipmentStatusMappingViewProps) {
  const { t } = useTranslation('shipmentStatusMappingModule');

  const hasMappings = allMappingsCount > 0;
  const hasFilteredResults = mappings.length > 0;
  const displayTotal = hasActiveFilters ? filteredTotal : allMappingsCount;
  const isSearchWithNoResults = hasMappings && !hasFilteredResults && !isFetchingFilters;

  return (
    <div className={configurationPageClass} data-testid="shipment-status-mapping-view">
      <div className="shrink-0">
        <ConfigurationPageHeader
          title={t('shipmentStatusMapping.pageTitle')}
          description={t('shipmentStatusMapping.pageDescription')}
          action={hasMappings && canUpdate ? <MapStatusesButton onClick={onMapStatuses} /> : undefined}
        />

        {hasMappings && (
          <div className="mt-8 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <FiltersSection
              couriers={couriers}
              search={search}
              courierId={courierId}
              statusId={statusId}
              onFiltersChange={onFiltersChange}
            />
            <TotalResults total={displayTotal} className="shrink-0 whitespace-nowrap py-0" />
          </div>
        )}
      </div>

      <div className={`${configurationPageContentClass} flex flex-col overflow-hidden pb-6`}>
        {isFetchingFilters ? (
          <div
            className="flex min-h-[12rem] flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-border bg-background"
            data-testid="shipment-status-mapping-filter-loading"
          >
            <Spinner size="md" />
            <p className="text-sm text-muted-foreground">{t('shipmentStatusMapping.messages.loadingMappings')}</p>
          </div>
        ) : !hasMappings ? (
          <ShipmentStatusMappingEmptyState canUpdate={canUpdate} onMapStatuses={onMapStatuses} />
        ) : isSearchWithNoResults ? (
          <div
            className="rounded-lg border border-dashed border-border bg-muted/20 px-6 py-10 text-center"
            data-testid="shipment-status-mapping-no-results"
          >
            <p className="text-sm font-medium text-foreground">
              {t('shipmentStatusMapping.emptyState.noResults.title')}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('shipmentStatusMapping.emptyState.noResults.description')}
            </p>
          </div>
        ) : (
          <ShipmentStatusMappingsList
            mappings={mappings}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onEditMapping={onEditMapping}
            onUnmapMapping={onUnmapMapping}
            canUpdate={canUpdate}
          />
        )}
      </div>
    </div>
  );
}
